<script>
import { showModal } from "@app/store/modalStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import UndoRedo from "./UndoRedo.svelte";
import { tick } from "svelte";
import autoFormat from "@cclib/functions/eventGroups/autoFormat.js";
import _Event from "@cclib/classes/event.js";
import insertEvent from "@cclib/functions/eventGroups/insertEvent.js";
import removeEvent from "@cclib/functions/eventGroups/removeEvent.js";
import stripTags from "@cclib/functions/quill/stripTags.js";
import splitIntoParagraphs from "@cclib/functions/quill/splitIntoParagraphs.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import * as m from "@app/paraglide/messages.js";

/** @type {{currentState: boolean, toggleMenu: (menu: string) => void}} */
let { currentState, toggleMenu } = $props();

function handleToggleMenu() {
    toggleMenu("edit");
}

function isBlankParagraph(paragraph) {
    const inner = paragraph.replace(/^<p\b[^>]*>/i, "").replace(/<\/p>$/i, "");
    return inner.replace(/<br\s*\/?>/gi, "").trim() === "";
}

function cleanEventText(html) {
    let text = html
        .replace(/<p([^>]*)>\s+/g, "<p$1>")
        .replace(/\s+<\/p>/g, "</p>");

    const paragraphs = splitIntoParagraphs(text);
    if (!paragraphs) return text;

    return paragraphs.filter((p) => !isBlankParagraph(p)).join("");
}

async function removeLeadingSpaces() {
    if (!eventGroupState[projectState.selected]) {
        return;
    }

    eventGroupState[projectState.selected].selected = [];
    eventGroupState[projectState.selected].events.forEach(
        (event, index, events) => {
            events[index].text = cleanEventText(event.text);
        },
    );

    eventGroupState[projectState.selected].events =
        eventGroupState[projectState.selected].events;

    insertHistoryAction({
        name: "remove leading spaces", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });
}

async function removeEmptyEvents(group) {
    if (!eventGroupState[group]) {
        return;
    }
    eventGroupState[projectState.selected].selected = [];
    eventGroupState[group].events = eventGroupState[group].events.filter(
        (event) => {
            return stripTags(event.text) !== "";
        },
    );

    insertHistoryAction({
        name: "remove events", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });
}

function pasteEvents() {
    try {
        if (!window.quillEditor) {
            return;
        }

        navigator.clipboard.readText().then((text) => {
            try {
                let clipboardEvents = JSON.parse(text);
                if (
                    clipboardEvents.length > 0 &&
                    clipboardEvents[0].id &&
                    clipboardEvents[0].style
                ) {
                    let currentTime = player ? player.currentTime : 0;
                    let newCurrentTime = currentTime;
                    Swal.fire({
                        ...SWAL_BASE,
                        customClass: SWAL_CLASSES.confirmCancel,
                        title: m.paste_events(),
                        text: m.paste_events_confirm(),
                        icon: "question",
                        showCancelButton: true,
                        confirmButtonText: m.yes(),
                        cancelButtonText: m.use_original_timing(),
                    })
                        .then((sAlertRes) => {
                            clipboardEvents.forEach((event, count, events) => {
                                if (sAlertRes.isConfirmed && currentTime >= 0) {
                                    let duration = event.end - event.start;
                                    let startTime =
                                        count === 0
                                            ? currentTime
                                            : currentTime +
                                              (event.start - events[0].end);

                                    startTime = startTime
                                        ? startTime
                                        : newCurrentTime;
                                    let endTime = startTime + duration;
                                    newCurrentTime = endTime;
                                    eventGroupState[projectState.selected] =
                                        insertEvent(
                                            eventGroupState[
                                                projectState.selected
                                            ],
                                            {
                                                ...event,
                                                start: startTime,
                                                end: endTime,
                                            },
                                            eventGroupState[
                                                projectState.selected
                                            ].selected[0] +
                                                count +
                                                1,
                                        );
                                } else {
                                    eventGroupState[projectState.selected] =
                                        insertEvent(
                                            eventGroupState[
                                                projectState.selected
                                            ],
                                            event,
                                            eventGroupState[
                                                projectState.selected
                                            ].selected[0] +
                                                count +
                                                1,
                                        );
                                }
                            });
                        })
                        .catch((err) => {
                            console.log("Error pasting events");
                            console.log(err);
                            console.log(err.message);
                        });
                } else {
                    throw new Error("No events found in clipboard");
                }
            } catch (err) {
                let range = window?.quillEditor?.getSelection();
                if (range && range.length === 0) {
                    window.quillEditor.insertText(range.index, text);
                } else if (range) {
                    window.quillEditor.deleteText(range.index, range.length);
                    window.quillEditor.insertText(range.index, text);
                }

                if (
                    editorState.autoFormatText &&
                    eventGroupState[projectState.selected].selected.length > 0
                ) {
                    let maxLines = eventGroupState[projectState.selected]
                            ? eventGroupState[projectState.selected].maxLines
                            : 2,
                        maxChars = eventGroupState[projectState.selected]
                            ? eventGroupState[projectState.selected].maxChars
                            : 32,
                        minDuration = 0,
                        allowOrphanWords = true;

                    try {
                        let selectedEvent =
                            eventGroupState[projectState.selected]
                                .selected[0];
                        eventGroupState[projectState.selected].selected = [
                            selectedEvent,
                        ];

                        eventGroupState[projectState.selected] = autoFormat(
                            eventGroupState[projectState.selected],
                            maxLines,
                            maxChars,
                            minDuration,
                            allowOrphanWords,
                            eventGroupState[projectState.selected].selected,
                        );

                        eventGroupState[projectState.selected].selected = [];
                        setTimeout(() => {
                            eventGroupState[projectState.selected].selected =
                                [selectedEvent];
                        }, 50);
                    } catch (err) {
                        console.log(err.message);
                    }
                }
            }

            insertHistoryAction({
                name: "paste events", //action name
                eventGroup: projectState.selected,
                snapshots: [
                    {
                        store: "eventGroupState",
                        value: JSON.stringify(eventGroupState),
                    },
                ],
            });
        });
    } catch (err) {
        console.log(err.message);
    }
}

async function copyEvents() {
    if (eventGroupState[projectState.selected].selected.length > 0) {
        let textToCopy,
            range = window?.quillEditor?.getSelection();
        if (
            range &&
            range.length !== 0 &&
            eventGroupState[projectState.selected].selected.length === 1
        ) {
            textToCopy = window.quillEditor.getText(range.index, range.length);
        } else {
            let eventsToCopy = [];
            eventGroupState[projectState.selected].selected
                .sort((eventA, eventB) => {
                    return eventA - eventB;
                })
                .forEach((eventIndex) => {
                    eventsToCopy.push(
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ],
                    );
                });

            textToCopy = JSON.stringify(eventsToCopy);
        }

        navigator.clipboard.writeText(textToCopy);
    }
}

async function cutEvents() {
    if (eventGroupState[projectState.selected].selected.length > 0) {
        let textToCopy,
            range = window?.quillEditor?.getSelection();
        if (
            range &&
            range.length !== 0 &&
            eventGroupState[projectState.selected].selected.length === 1
        ) {
            textToCopy = window.quillEditor.getText(range.index, range.length);
            window.quillEditor.deleteText(range.index, range.length);
        } else {
            let eventsToCut = [];
            let selectedEvents = eventGroupState[
                projectState.selected
            ].selected.map((selectedEvent) => {
                return selectedEvent;
            });

            eventGroupState[projectState.selected].selected = [];
            await tick();
            selectedEvents
                .sort((a, b) => {
                    return b - a;
                })
                .forEach((eventIndex) => {
                    eventsToCut.push(
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ],
                    );
                    eventGroupState[projectState.selected] = removeEvent(
                        eventGroupState[projectState.selected],
                        eventIndex,
                    );
                });

            textToCopy = JSON.stringify(eventsToCut);
        }

        navigator.clipboard.writeText(textToCopy);

        insertHistoryAction({
            name: "cut event(s)", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    }
}
</script>

<li class="nav-item dropdown {currentState ? 'show' : ''}" role="presentation">
    <a onclick={handleToggleMenu} 
       class="dropdown-toggle nav-link" 
       href="#!/"
       id="edit-menu"
       role="button"
       aria-haspopup="true"
       aria-expanded={currentState}>{m.edit()}</a>
    <div class="dropdown-menu {currentState ? 'show' : ''}" 
         role="menu" 
         aria-labelledby="edit-menu">
        <UndoRedo></UndoRedo>
        <hr class="dropdown-divider" />
        <a class="dropdown-item" href="#!/" onclick={cutEvents} role="menuitem" id="edit-cut">
            <i class="bi bi-scissors" aria-hidden="true"></i> {m.cut()}</a>
        <a class="dropdown-item" href="#!/" onclick={copyEvents} role="menuitem" id="edit-copy">
            <i class="bi bi-clipboard-plus" aria-hidden="true"></i> {m.copy()}</a>
        <a class="dropdown-item" href="#!/" onclick={pasteEvents} role="menuitem" id="edit-paste">
            <i class="bi bi-clipboard-check" aria-hidden="true"></i> {m.paste()}</a>
        <hr class="dropdown-divider" />
        <a class="dropdown-item" 
           href="#!/" 
           onclick={() => removeEmptyEvents(projectState.selected)}
           role="menuitem"
           id="edit-remove-empty">{m.remove_empty_events()}</a>
        <a class="dropdown-item" 
           href="#!/" 
           onclick={() => showModal("removeSdh")}
           role="menuitem"
           id="edit-remove-sdh">{m.remove_sdh()}</a>
        <a class="dropdown-item" 
           href="#!/" 
           onclick={removeLeadingSpaces}
           role="menuitem"
           id="edit-remove-spaces">{m.remove_leading_spaces()}</a>
        <hr class="dropdown-divider"/>
        <a class="dropdown-item {eventGroupState[projectState.selected] ? '' : 'disabled'}" 
           href="#!/" 
           onclick={() => showModal("goToEvent")}
           role="menuitem"
           id="edit-goto"
           aria-disabled={!eventGroupState[projectState.selected]}>{m.go_to_event()}</a>
        {#if !environment.iframe}
        <hr class="dropdown-divider"/>
        {#if authState.team && environment.online}
        <a class="dropdown-item" 
           href="#!/" 
           onclick={() => showModal("knpManager")}
           role="menuitem"
           id="edit-knp-manager">
            <i class="bi bi-journal-text" aria-hidden="true"></i> {m.knp_manager()}</a>
        {/if}
        <a class="dropdown-item" 
           href="#!/" 
           onclick={() => showModal("autoReplace")}
           role="menuitem"
           id="edit-auto-replace">
            <i class="bi bi-arrow-repeat" aria-hidden="true"></i> {m.auto_replacement()}</a>        
        <a class="dropdown-item" 
           href="#!/" 
           onclick={() => showModal("presetManager")}
           role="menuitem"
           id="edit-preset-manager">
            <i class="bi bi-upload"></i> {m.export_presets()}</a>
        <a class="dropdown-item" 
           href="#!/" 
           onclick={() => showModal("shortcutKeys")}
           role="menuitem"
           id="edit-shortcuts">
            <i class="bi bi-keyboard" aria-hidden="true"></i> {m.shortcut_keys()}</a>
        <a class="dropdown-item" 
           href="#!/" 
           onclick={() => showModal("options")}
           role="menuitem"
           id="edit-options">
            <i class="bi bi-sliders" aria-hidden="true"></i> {m.options()}</a>
        {/if}
    </div>
</li>
